import React, { useCallback, useState } from 'react';
import Box from 'carbon-react/lib/components/box';
import Button from 'carbon-react/lib/components/button/';
import { SearchEvent } from 'carbon-react/lib/components/search';

import { FormattedMessage, useIntl } from '@futrli/localisation';
import { UILoader } from '@futrli/ui-components';

import { DriverActionsProvider, useDriverActions } from './components/context/driver-actions-context';
import { DriverReorderProvider, useDriverReorder } from './components/context/driver-reorder-context';
import { DriversDataGrid } from './components/drivers-grid/drivers-data-grid';
import { DriversToolbar } from './components/drivers-grid/drivers-toolbar';
import { DriversViewProvider, useDriversView } from './components/context/drivers-view-context';
import { NonFinancialDriverTree } from './types';
import { PageHeader } from './components/page-header/page-header';
import { VirtualizedCardListEmptyState } from '../ui/virtualized-card-list/virtualized-card-list-empty-state';

const NonFinancialDriversContent = () => {
  const { handleCreateDriver, handleCreateGroup } = useDriverActions();
  const { columns, isUpdatingTree, startColumn } = useDriversView();
  const {
    isReordering,
    draftTree,
    flatData,
    drag,
    sourceTree,
    startReordering,
    cancelReordering,
    saveReordering,
    isSaving
  } = useDriverReorder();

  const intl = useIntl();

  const [searchText, setSearchText] = useState('');

  const handleSearchChange = useCallback((event: SearchEvent) => {
    setSearchText(event.target.value || '');
  }, []);

  const treeToDisplay = isReordering && draftTree ? draftTree.tree : sourceTree.tree;

  if (treeToDisplay.length === 0) {
    return (
      <Box margin="auto" padding="32px">
        <PageHeader onCreateClick={handleCreateDriver} />
        <VirtualizedCardListEmptyState
          title={intl.formatMessage({
            id: 'drivers.no_drivers_to_show',
            defaultMessage: 'No drivers to show'
          })}
          description={intl.formatMessage({
            id: 'drivers.get_started_with_your_first_non_financial_driver',
            defaultMessage: 'Get started with your first non-financial driver'
          })}
          action={
            <Button type="secondary" onClick={handleCreateDriver}>
              <FormattedMessage id="predict.create_non_financial_driver" defaultMessage="Create non-financial driver" />
            </Button>
          }
        />
      </Box>
    );
  }
  return (
    <Box margin="auto" padding="32px">
      <PageHeader onCreateClick={handleCreateDriver} />
      <DriversToolbar
        searchText={searchText}
        onSearchChange={handleSearchChange}
        onReorderClick={startReordering}
        onCreateGroupClick={handleCreateGroup}
        isReordering={isReordering}
        isUpdating={isUpdatingTree || isSaving}
        onSaveReorder={saveReordering}
        onCancelReorder={cancelReordering}
      />
      <DriversDataGrid
        tree={treeToDisplay}
        columns={columns}
        startColumn={startColumn}
        searchText={searchText}
        isReordering={isReordering}
        isUpdating={isUpdatingTree || isSaving}
        drag={drag}
        flatData={flatData}
      />
    </Box>
  );
};

const NonFinancialDriversWithReorder = () => {
  const { driverTree, isLoading, isUpdatingTree } = useDriversView();

  if (isLoading) {
    return <UILoader isFull />;
  }

  if (!driverTree) {
    return null;
  }

  return (
    <DriverReorderProvider tree={(driverTree as unknown) as NonFinancialDriverTree} isUpdating={isUpdatingTree}>
      <NonFinancialDriversContent />
    </DriverReorderProvider>
  );
};

export const NonFinancialDrivers = () => {
  return (
    <DriversViewProvider>
      <DriverActionsProvider>
        <NonFinancialDriversWithReorder />
      </DriverActionsProvider>
    </DriversViewProvider>
  );
};
